/**
 * 图片滤镜插件内部 Canvas 与素材 ID 工具
 *
 * 所有方法仅操作传入数据，不持有跨编辑器全局状态。
 */
import { createCanvas } from '@/utils/file-kit';
import { MaterialIdsType, type ILayerProps } from '@/version-types';

/**
 * 按强度把 100% 滤镜结果叠加到源图
 *
 * @param sourceCanvas - 未叠加 mask 和图片特效的源图 Canvas
 * @param filterCanvas - 离线生成的 100% 滤镜结果 Canvas
 * @param alpha - 滤镜强度，范围 0-1
 * @returns 独立的强度合成 Canvas
 */
export const composeFilterCanvas = (
    sourceCanvas: HTMLCanvasElement,
    filterCanvas: HTMLCanvasElement,
    alpha: number,
) => {
    const { width, height, canvas, ctx } = createCanvas(sourceCanvas.width, sourceCanvas.height);
    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = 1;
    ctx.drawImage(sourceCanvas, 0, 0);
    ctx.globalAlpha = Math.min(1, Math.max(0, alpha));
    ctx.drawImage(filterCanvas, 0, 0);
    ctx.globalAlpha = 1;
    return canvas;
};

/**
 * 复制 Canvas，避免滤镜桥修改会话源图
 *
 * @param sourceCanvas - 待复制 Canvas
 * @returns 与源图尺寸相同的独立 Canvas
 */
export const cloneFilterCanvas = (sourceCanvas: HTMLCanvasElement) =>
    createCanvas(sourceCanvas.width, sourceCanvas.height, sourceCanvas).canvas;

/**
 * 向图层追加已应用滤镜素材 ID
 *
 * @param layer - 待更新图层
 * @param filterId - 已应用滤镜 ID
 * 副作用：原地更新传入图层的 materialIds。
 */
export const appendAppliedFilterId = (layer: ILayerProps, filterId: string) => {
    if (!filterId) return;
    layer.materialIds ??= {};
    const filterIds = layer.materialIds[MaterialIdsType.FILTER] ?? [];
    if (!filterIds.includes(filterId)) filterIds.push(filterId);
    layer.materialIds[MaterialIdsType.FILTER] = filterIds;
};
